import { useCallback, useEffect, useState } from "react";

export type ThemePref = "light" | "dark" | "system";

function systemPrefersDark(): boolean {
  return typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches;
}

const STORAGE_KEY = "jilian-theme";

/** 主题切换（默认跟随系统）。阶段 1 用 class 切换 + prefers-color-scheme 联动。 */
export function useTheme() {
  const [pref, setPrefState] = useState<ThemePref>(() => {
    const stored = typeof localStorage !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    return stored === "light" || stored === "dark" || stored === "system" ? stored : "system";
  });

  const apply = useCallback((p: ThemePref) => {
    const dark = p === "dark" || (p === "system" && systemPrefersDark());
    document.documentElement.classList.toggle("dark", dark);
  }, []);

  useEffect(() => {
    apply(pref);
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => apply(pref);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [pref, apply]);

  const setPref = useCallback((p: ThemePref) => {
    try {
      localStorage.setItem(STORAGE_KEY, p);
    } catch {
      /* 隐私模式等场景忽略 */
    }
    setPrefState(p);
  }, []);

  const toggle = useCallback(() => {
    setPref(pref === "dark" ? "light" : "dark");
  }, [pref, setPref]);

  return { pref, setPref, toggle, isDark: pref === "dark" || (pref === "system" && systemPrefersDark()) };
}
